import { ContentFlagReason } from "../../../common/contentTypes";

export interface ParsedFlagRow {
  line: number;
  questionId?: string;
  questionTitle?: string;
  reason: ContentFlagReason;
  comment?: string;
  customer?: string;
  /**
   * Only ever "resolved": the parser does not emit "open" (absent means that)
   * and the server acts on "resolved" alone, so both ends say the same thing.
   *
   * Required rather than optional even though the value may be undefined. The
   * parser always writes the key, and requiring it is what makes a reshape of
   * the rows that forgets to carry it a compile error instead of a silent drop
   * that re-opens every historically-resolved flag.
   */
  status: "resolved" | undefined;
}

export interface FlagCsvResult {
  rows: ParsedFlagRow[];
  /** Per-line problems. A bad row is reported and skipped, never guessed at. */
  errors: string[];
}

/**
 * Reason wording in a support export is human, not enum — "wrong answer",
 * "Wrong Answer", "too-hard". Anything unrecognised is an error rather than a
 * silent fallback to "other", because the reason drives whether a flag becomes a
 * generation counter-example.
 */
const REASONS: Record<string, ContentFlagReason> = {
  inappropriate: "inappropriate",
  wrong_answer: "wrong_answer",
  "wrong answer": "wrong_answer",
  wronganswer: "wrong_answer",
  too_hard: "too_hard",
  "too hard": "too_hard",
  toohard: "too_hard",
  other: "other",
};

/** Splits one CSV line, honouring double quotes and escaped ("") quotes. */
export const splitCsvLine = (line: string): string[] => {
  const cells: string[] = [];
  let cell = "";
  let quoted = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (quoted) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"') {
      quoted = true;
    } else if (char === ",") {
      cells.push(cell);
      cell = "";
    } else {
      cell += char;
    }
  }
  cells.push(cell);

  return cells.map((value) => value.trim());
};

const HEADER_ALIASES: Record<string, string> = {
  questionid: "questionId",
  question_id: "questionId",
  id: "questionId",
  title: "questionTitle",
  question: "questionTitle",
  questiontitle: "questionTitle",
  reason: "reason",
  comment: "comment",
  comments: "comment",
  note: "comment",
  customer: "customer",
  org: "customer",
  organisation: "customer",
  organization: "customer",
  status: "status",
};

/**
 * Parses a flags CSV for backfill. Requires a header row and a `reason`
 * column, plus either a questionId or a title so the flag can name what it is
 * about. Column order is free and unknown columns are ignored, because the
 * export comes from a support tool rather than from us.
 *
 * The exact shape is not frozen yet (tech doc Q6 waits on a sample of the real
 * backfill), so this stays permissive about naming and strict about meaning.
 */
export const parseFlagCsv = (text: string): FlagCsvResult => {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return { rows: [], errors: ["The file is empty"] };
  }

  const header = splitCsvLine(lines[0]).map(
    (name) => HEADER_ALIASES[name.toLowerCase().replace(/\s+/g, "")] ?? name,
  );

  if (!header.includes("reason")) {
    return {
      rows: [],
      errors: [
        `No "reason" column found. Expected a header row with reason plus questionId or title; saw: ${header.join(
          ", ",
        )}`,
      ],
    };
  }
  if (!header.includes("questionId") && !header.includes("questionTitle")) {
    return {
      rows: [],
      errors: [
        "Every flag needs a questionId or a title column so it can name the question it is about",
      ],
    };
  }

  const rows: ParsedFlagRow[] = [];
  const errors: string[] = [];

  lines.slice(1).forEach((line, index) => {
    const lineNumber = index + 2; // 1-based, and the header is line 1
    const cells = splitCsvLine(line);
    const value = (name: string) => {
      const at = header.indexOf(name);
      return at === -1 ? "" : (cells[at] ?? "").trim();
    };

    const rawReason = value("reason").toLowerCase();
    const reason = REASONS[rawReason];
    if (!reason) {
      errors.push(
        `Line ${lineNumber}: unrecognised reason "${value(
          "reason",
        )}" (expected wrong answer, too hard, inappropriate or other)`,
      );
      return;
    }

    const questionId = value("questionId");
    const questionTitle = value("questionTitle");
    if (!questionId && !questionTitle) {
      errors.push(`Line ${lineNumber}: no questionId or title`);
      return;
    }

    const status = value("status").toLowerCase();
    rows.push({
      line: lineNumber,
      questionId: questionId || undefined,
      questionTitle: questionTitle || undefined,
      reason,
      comment: value("comment") || undefined,
      customer: value("customer") || undefined,
      status: status === "resolved" ? "resolved" : undefined,
    });
  });

  return { rows, errors };
};
